/* eslint-disable max-lines */
import { Theme } from '@components/AppBar/app-bar-state';
import { ZERO } from '@constants';
import { createTheme } from '@mui/material';
import { red } from '@mui/material/colors';

const THEME_COMMON_COLORS = {
  contrastText: '#FFFFFF',
  black: '#000D1A',
  primaryDark: '#0079E8',
  primaryLight: '#0057A7',
  divider: '#FFFFFF1F',
};

const THEME_PALETTES = {
  darkTheme: {
    primary: {
      main: THEME_COMMON_COLORS.primaryDark,
      dark: THEME_COMMON_COLORS.primaryLight,
      light: '#80C1FF',
      contrastText: THEME_COMMON_COLORS.contrastText,
    },
    secondary: {
      main: '#9C40D6',
      dark: '#AE5EE0',
      light: '#C38EE5',
      contrastText: THEME_COMMON_COLORS.contrastText,
    },
    text: {
      primary: '#E4E9ED',
      secondary: '#FFFFFF84',
      disabled: '#FFFFFF61',
      link: THEME_COMMON_COLORS.primaryDark,
      tretiary: '#FFFFFF8C',
    },
    divider: THEME_COMMON_COLORS.divider,
    action: {
      active: `${THEME_COMMON_COLORS.contrastText}8F`,
      hover: `${THEME_COMMON_COLORS.contrastText}14`,
      selected: `${THEME_COMMON_COLORS.contrastText}29`,
      focus: `${THEME_COMMON_COLORS.contrastText}1F`,
      disabledBackground: THEME_COMMON_COLORS.divider,
      disabled: `${THEME_COMMON_COLORS.contrastText}61`,
    },
    error: {
      main: red[500],
      dark: red[700],
      light: red[500],
      contrastText: THEME_COMMON_COLORS.black,
    },
    info: {
      main: '#29B6F6',
      dark: '#0288D1',
      light: '#4FC3F7',
      contrastText: '#000D1ADE',
    },
    success: {
      main: '#77B224',
      dark: '#568F1D',
      light: '#82CD19',
      contrastText: '#000D1ADE',
    },
    common: {
      black: THEME_COMMON_COLORS.contrastText,
      white: THEME_COMMON_COLORS.black,
    },
    background: {
      default: THEME_COMMON_COLORS.black,
      paper: '#0E1924',
    },
    customGrey: {
      main: '#AEB5BD',
      dark: '#AEB5BD',
      light: '#AEB5BD',
      contrastText: '#AEB5BD',
    },
    textButton: {
      main: '#FFFFFFB2',
      dark: '#FFFFFFB2',
      light: '#FFFFFFB2',
      contrastText: '#FFFFFFB2',
    },
  },
  lightTheme: {
    primary: {
      main: THEME_COMMON_COLORS.primaryLight,
      dark: '#004686',
      light: THEME_COMMON_COLORS.primaryDark,
      contrastText: THEME_COMMON_COLORS.contrastText,
    },
    secondary: {
      main: '#812CB8',
      dark: '#6E2B99',
      light: '#AE5EE0',
      contrastText: THEME_COMMON_COLORS.contrastText,
    },
    text: {
      primary: '#0F172A',
      secondary: '#000D1AB3',
      disabled: '#000D1A61',
      link: '#455A64',
      tretiary: '#000D1A8C',
    },
    divider: '#000D1A1F',
    action: {
      active: '#00172E8F',
      hover: `${THEME_COMMON_COLORS.black}0A`,
      selected: `${THEME_COMMON_COLORS.black}14`,
      focus: `${THEME_COMMON_COLORS.black}1F`,
      disabledBackground: '#0000001F',
      disabled: `${THEME_COMMON_COLORS.black}61`,
    },
    error: {
      main: red[700],
      dark: red[800],
      light: red[400],
      contrastText: THEME_COMMON_COLORS.contrastText,
    },
    info: {
      main: '#0288D1',
      dark: '#01579B',
      light: '#03A9F4',
      contrastText: THEME_COMMON_COLORS.contrastText,
    },
    success: {
      main: '#61A120',
      dark: '#417D19',
      light: '#82CD19',
      contrastText: THEME_COMMON_COLORS.contrastText,
    },
    common: {
      black: THEME_COMMON_COLORS.black,
      white: THEME_COMMON_COLORS.contrastText,
    },
    background: {
      default: THEME_COMMON_COLORS.contrastText,
      paper: '#F8F8F8',
    },
    customGrey: {
      main: '#AEB5BD',
      dark: '#AEB5BD',
      light: '#AEB5BD',
      contrastText: '#AEB5BD',
    },
    textButton: {
      main: '#000D1AB2',
      dark: '#000D1AB2',
      light: '#000D1AB2',
      contrastText: '#000D1AB2',
    },
  },
};

const typography = {
  fontFamily: 'Montserrat, Arial',
  body2: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20.02px',
    letterSpacing: '0.17px',
  },
  body1: {
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '22.5px',
    letterSpacing: '0.15px',
  },
  h4: {
    fontSize: '24px',
    fontWeight: 400,
    lineHeight: '133.4%',
  },
  h5: {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '32.02px',
    letterSpacing: '0.15px',
  },
  h6: {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },
  h7: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '27px',
  },
  subtitle1: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '28px',
    letterSpacing: '0.15px',
  },
  subtitle2: {
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '157%',
    letterSpacing: '0.1px',
  },
  subtitle14: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '21.98px',
    letterSpacing: '0.1px',
  },
  subtitle16: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '28px',
    letterSpacing: '0.15px',
  },
  subtitle18: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '175%',
    letterSpacing: '0.15px',
  },
  subtitle20: {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '28px',
  },
  caption: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '19.92px',
  },
  overline: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  header: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  medium: {
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '24px',
  },
};

export const getTheme = (theme: Theme) => {
  const isDarkMode = theme === Theme.Dark;
  const palette = isDarkMode ? THEME_PALETTES.darkTheme : THEME_PALETTES.lightTheme;

  return createTheme({
    palette: {
      ...palette,
      customGrey: palette.customGrey,
      mode: isDarkMode ? 'dark' : 'light',
    },
    typography,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            textTransform: 'none',

            '& .MuiCircularProgress-root': {
              width: '20px !important',
              height: '20px !important',
            },
          },
        },
        defaultProps: {
          size: 'small',
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '22px',
            },
          },
          {
            props: { size: 'medium' },
            style: {
              fontSize: '15px',
              fontWeight: '600',
              lineHeight: '24px',
            },
          },
          {
            props: { size: 'large' },
            style: {
              fontSize: '16px',
              fontWeight: '600',
              lineHeight: '26px',
            },
          },
          {
            props: { variant: 'outlined', color: 'primary' },
            style: {
              borderColor: isDarkMode ? '#80C1FF99' : '#2896FC80',
              color: isDarkMode ? '#2894F7' : palette.primary.main,
            },
          },
        ],
      },
      MuiPaper: {
        defaultProps: {
          square: true,
          sx: {
            borderRadius: '4px',
          },
        },
        variants: [
          {
            props: { elevation: 1 },
            style: {
              boxShadow: '0px 1px 1px -1px #000D1A33, 0px 0px 1px 0px #000D1A24, 0px 0px 3px 0px #0057A71F',
            },
          },
        ],
      },
      MuiFab: {
        defaultProps: {
          size: 'medium',
          variant: 'extended',
          sx: {
            textTransform: 'none',
            color: palette.text.primary,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: '#80C1FF14',
              color: isDarkMode ? palette.text.primary : palette.primary.main,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: '#2896fc14',
            },
            '&:hover': {
              backgroundColor: '#2896fc0a',
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '.MuiTableCell-head': {
              backgroundColor: isDarkMode ? '#111C26' : '#F5F5F5',
              borderBottom: 'none',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          root: {
            color: isDarkMode ? '#636C75' : '#D3DAE0',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDarkMode ? '#363C4DE5' : '#0F172ACC',
            color: isDarkMode ? '#E4E9ED' : '#FFFFFF',
            maxWidth: '400px',
          },
          arrow: {
            fontSize: '13px',
            '&::before': {
              backgroundColor: isDarkMode ? '#363C4DE5' : '#0F172ACC',
            },
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          PaperProps: {
            sx: {
              backgroundColor: isDarkMode ? '#202F3D' : '#FFFFFF',
            },
          },
        },
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(4px)',
          },
          paper: {
            borderRadius: 8,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '24px 32px',
            ...typography.h6,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '0 32px 20px',
            color: palette.text.primary,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            ...typography.body1,
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '8px 32px 32px',
            display: 'flex',
            justifyContent: 'flex-end',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            '&::before': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        },
        defaultProps: {
          elevation: ZERO,
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '8px 16px 0px 16px',
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: 14,
            color: palette.text.primary,
            lineHeight: '40px',
          },
        },
      },
      MuiToggleButton: {
        variants: [
          {
            props: { size: 'small' },
            style: {
              height: '36px',
            },
          },
          {
            props: { size: 'medium' },
            style: {
              height: '40px',
            },
          },
        ],
      },
    },
  });
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    h7: React.CSSProperties;
    subtitle14: React.CSSProperties;
    subtitle16: React.CSSProperties;
    subtitle20: React.CSSProperties;
    header: React.CSSProperties;
    medium: React.CSSProperties;
    subtitle18: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    h7?: React.CSSProperties;
    subtitle14?: React.CSSProperties;
    subtitle16?: React.CSSProperties;
    subtitle20?: React.CSSProperties;
    header?: React.CSSProperties;
    medium?: React.CSSProperties;
    subtitle18?: React.CSSProperties;
  }

  interface Palette {
    customGrey: Palette['primary'];
  }

  interface PaletteOptions {
    customGrey?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h7: true;
    subtitle14: true;
    subtitle16: true;
    subtitle20: true;
    overline: true;
    header: true;
    medium: true;
    subtitle18: true;
  }

  interface TypographyPropsColorOverrides {
    textLink: true;
    textTretiary: true;
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    customGrey: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    textButton: true;
  }
}
